import type { ErrorInfo, ReactNode } from 'react';
import { Component, useState } from 'react';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ERROR_EVENTS } from '../constants/analyticsEvents';
import { categorizeComponentError, getErrorHash } from '../types/componentErrors';
import { clearPWACaches } from '@utils/storageCleanup';
import { logger } from '@utils/logger';
import { FunnyLoadingMessage } from './FunnyLoadingMessage';
import {
  isChunkLoadError,
  isMimeTypeError,
  handleChunkLoadError,
} from '@utils/chunkLoadErrorHandler';
import { isReactCompilerBuild } from '@lib/webVitals';
import { removeStaticTransitShell } from '@lib/magicLink/staticTransitShell';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  trackEvent?: (
    eventName: string,
    properties?: Record<string, string | number | boolean | null>,
  ) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  isAutoRecovering?: boolean;
}

const DefaultFallback = ({ trackEvent }: { trackEvent?: ErrorBoundaryProps['trackEvent'] }) => {
  const [isClearing, setIsClearing] = useState(false);

  const handleReload = async () => {
    setIsClearing(true);

    // Track reload event
    trackEvent?.(ERROR_EVENTS.BOUNDARY_RELOAD, {
      timestamp: Date.now(),
    });

    // Ensure minimum loading UI visibility for better UX
    const minDelay = new Promise(resolve => setTimeout(resolve, 800));

    try {
      // Clear PWA caches with timeout, then hard reload regardless
      await Promise.all([clearPWACaches(), minDelay]);
    } catch (error) {
      logger.error('Cache clearing encountered an error, proceeding with reload:', error);
      await minDelay; // Ensure minimum delay even on error
    }

    // Hard reload to bypass all caches - use cache-busting timestamp
    const baseUrl = window.location.href.split('?')[0];
    window.location.href = `${baseUrl}?t=${Date.now()}`;
  };

  if (isClearing) {
    return (
      <div className="min-h-screen bg-mb-bg flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white shadow-xl rounded-2xl p-8 text-center space-y-4">
          <FunnyLoadingMessage type="errorRecovery" className="text-gray-700" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mb-bg flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white shadow-xl rounded-2xl p-8 text-center space-y-4">
        <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
        <p className="text-sm text-gray-600">
          Please refresh the page. If the problem continues, contact support and share any details
          shown below.
        </p>
        <button
          type="button"
          className="w-full rounded-full bg-mb-teal py-2.5 text-mb-ink font-medium hover:bg-mb-teal-dark transition-colors"
          onClick={handleReload}
        >
          Reload Meetball
        </button>
      </div>
    </div>
  );
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
    isAutoRecovering: false,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Check if this is a chunk load error - we'll auto-recover
    if (isChunkLoadError(error) || isMimeTypeError(error)) {
      return { hasError: true, error, isAutoRecovering: true };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // AIDEV-NOTE [MEETBALL magic-link]: FIRST, above every branch below, including
    // the chunk-load early return.
    //
    // index.html paints #mb-transit-shell on /callback and /auth/link before any JS
    // runs, as a SIBLING of #app at `position: fixed; inset: 0; z-index: 1`, and the
    // two screens that own the handover remove it on their first commit. Neither
    // commits if something above the router throws -- and this fallback renders
    // inside #app, in flow, with no stacking context, so it loses to that z-index
    // and the visitor gets "Signing you in" over a Reload button they can neither
    // see nor press, permanently. Removing the node rather than out-stacking it:
    // a z-index here would leave a full-viewport cream sheet in the document over
    // whatever the app paints next. The shell also carries `pointer-events: none`,
    // which is the floor under this, not a substitute for it.
    removeStaticTransitShell();

    this.setState({ errorInfo });

    // If it's a chunk load error, trigger automatic recovery
    if (isChunkLoadError(error) || isMimeTypeError(error)) {
      logger.info('ErrorBoundary: Chunk load error detected, auto-recovering...');
      void handleChunkLoadError(error);
      return;
    }

    const errorCategory = categorizeComponentError(error);
    const errorHash = getErrorHash(errorInfo.componentStack ?? undefined);
    const urlPath = window.location.pathname;

    const debugPayload = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      errorCategory,
      errorHash,
    };

    logger.error('App Error Boundary captured an error', debugPayload);

    // Track error with Amplitude (minimal properties, no PII)
    // AIDEV-NOTE: react_compiler flag enables A/B comparison during migration
    this.props.trackEvent?.(ERROR_EVENTS.BOUNDARY_TRIGGERED, {
      errorCategory,
      errorType: error.name || 'Error',
      errorHash,
      urlPath,
      timestamp: Date.now(),
      react_compiler: isReactCompilerBuild,
    });

    if (import.meta.env.VITE_DEBUG_MODE) {
      // Allow staging observers to hook into detailed error telemetry
      window.dispatchEvent(
        new CustomEvent('meetball:error', {
          detail: debugPayload,
        }),
      );
    }
  }

  resetError = () => {
    // Track reset/dismiss event
    this.props.trackEvent?.(ERROR_EVENTS.BOUNDARY_RESET, {
      timestamp: Date.now(),
    });

    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      const { fallback, trackEvent } = this.props;
      const isDebug = Boolean(import.meta.env.VITE_DEBUG_MODE);

      // Show auto-recovery UI for chunk load errors
      if (this.state.isAutoRecovering) {
        return (
          <div className="min-h-screen bg-mb-bg flex items-center justify-center p-6">
            <div className="w-full max-w-md bg-white shadow-xl rounded-2xl p-8 text-center space-y-4">
              <div className="w-10 h-10 border-3 border-gray-200 border-t-mb-teal rounded-full mx-auto animate-spin" />
              <p className="text-gray-700 font-medium">Updating to latest version...</p>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen bg-mb-bg">
          {fallback ?? <DefaultFallback trackEvent={trackEvent} />}
          {isDebug && this.renderDebugPanel()}
        </div>
      );
    }

    return this.props.children;
  }

  private renderDebugPanel() {
    const { error, errorInfo } = this.state;

    if (!error) {
      return null;
    }

    return (
      <div className="mx-auto max-w-2xl mt-6 px-6">
        <div className="bg-white border border-red-200 rounded-xl shadow-xs p-6 overflow-x-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-red-600">Debug details</h2>
            <button
              type="button"
              className="text-sm text-mb-teal hover:underline"
              onClick={this.resetError}
            >
              Dismiss and try again
            </button>
          </div>
          <pre className="text-xs text-gray-800 whitespace-pre-wrap wrap-break-word leading-5">
            {error.message}
            {'\n\n'}
            {error.stack}
            {errorInfo?.componentStack && `\n\nComponent stack:\n${errorInfo.componentStack}`}
          </pre>
        </div>
      </div>
    );
  }
}

/**
 * Error boundary wrapper with Amplitude tracking
 * Use this component to wrap your app and track errors
 */
export const ErrorBoundaryWithTracking = ({
  children,
  fallback,
}: Omit<ErrorBoundaryProps, 'trackEvent'>) => {
  const { trackEvent } = useAmplitudeTracking();

  return (
    <ErrorBoundary trackEvent={trackEvent} fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundaryWithTracking;
